import { useState } from 'react';
import { branches, services } from '../data';

function SVGIcon({ name, className = 'w-5 h-5' }) {
  const icons = {
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    mapPin: <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0zM12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />,
    zap: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />,
    check: <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3" />,
    phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />,
    mail: <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6" />,
  };
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      {icons[name] || icons.mapPin}
    </svg>
  );
}

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    service: '',
    details: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;

    if (name === 'mobile') {
      // Strip all non-numeric characters and restrict to maximum 10 digits
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, mobile: digitsOnly }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }

  function validate() {
    const newErrors = {};

    if (!formData.name.trim() || formData.name.trim().length < 3) {
      newErrors.name = 'Please enter your full name (at least 3 characters).';
    }

    const cleanMobile = formData.mobile.replace(/\D/g, '');
    if (!cleanMobile || cleanMobile.length !== 10) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number.';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a required service program.';
    }

    if (!formData.details.trim()) {
      newErrors.details = 'Please describe your premise size or pest problem.';
    }

    return newErrors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setErrors({});
      setIsSubmitting(true);
      
      try {
        const response = await fetch('https://formspree.io/f/xppzkbkw', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(formData),
        });
        
        if (response.ok) {
          setIsSubmitted(true);
        } else {
          setErrors({ form: 'Oops! There was a problem submitting your form. Please try again later.' });
        }
      } catch (error) {
        setErrors({ form: 'Oops! There was a problem submitting your form. Please try again later.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  }

  return (
    <main className="bg-bg overflow-hidden">
      {/* Hero */}
      <section className="relative min-h-[320px] sm:min-h-[380px] md:min-h-[440px] flex items-center px-3 min-[480px]:px-5 sm:px-6 md:px-16 py-12 sm:py-16 md:py-20 overflow-hidden bg-gradient-to-b from-primary-dark via-primary to-primary-deep text-white">
        <div className="absolute inset-0 opacity-15 bg-[radial-gradient(var(--color-accent-bright)_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none" />
        <div className="absolute right-[10%] top-[10%] w-48 sm:w-72 h-48 sm:h-72 rounded-full bg-accent-bright/15 blur-3xl pointer-events-none float-slow" />
        <div className="absolute left-[5%] bottom-[5%] w-40 sm:w-60 h-40 sm:h-60 rounded-full bg-cta/20 blur-3xl pointer-events-none float-medium" />

        <div className="relative z-10 max-w-3xl mx-auto text-center animate-rise">
          <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-2 rounded-full bg-accent/20 text-accent-bright text-[9px] min-[480px]:text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3 sm:mb-5 border border-accent/30 shadow-sm backdrop-blur-md">
            <span className="w-1.5 h-1.5 sm:w-2.5 sm:h-2.5 rounded-full bg-accent-bright animate-ping shrink-0" />
            24/7 SUPPORT &amp; DISPATCH
          </span>
          <h1 className="font-heading text-2xl min-[480px]:text-3xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-3 sm:mb-5 tracking-tight px-2 sm:px-0">
            We are ready to <span className="text-accent-bright">help.</span>
          </h1>
          <p className="text-white/80 text-[11px] min-[480px]:text-xs sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-normal px-2 sm:px-0">
            Contact our Pune head office to arrange an immediate inspection or discuss customized treatment plans across Maharashtra.
          </p>
        </div>
      </section>

      {/* Curved Divider Top */}
      <div className="w-full overflow-hidden leading-none bg-primary-dark -mt-1 -mb-1 relative z-10 pointer-events-none">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-14 text-accent-bright fill-current">
          <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,60 L1200,120 L0,120 Z" />
        </svg>
      </div>

      {/* Redesigned Contact Form & Hotline Section */}
      <section className="py-12 sm:py-16 md:py-20 px-3 min-[480px]:px-5 sm:px-6 md:px-16 bg-surface-alt relative overflow-hidden">
        {/* Ambient Glowing Background Bulb */}
        <div className="absolute right-0 top-1/4 w-60 sm:w-96 h-60 sm:h-96 bg-accent-bright/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 items-center relative z-10">

          {/* Left Column: Direct Hotline & Office Details */}
          <div className="lg:col-span-6 space-y-4 sm:space-y-6 md:space-y-8">
            <div>
              <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-accent-soft text-accent-muted text-[9px] min-[480px]:text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3 sm:mb-4 border border-accent/30 shadow-2xs">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-cta animate-ping" />
                PUNE HEADQUARTERS &amp; DISPATCH
              </span>
              <h2 className="font-heading text-xl min-[480px]:text-2xl sm:text-3xl md:text-5xl font-extrabold text-primary tracking-tight mb-2.5 sm:mb-4 leading-tight">
                Speak with an <span className="text-cta">expert today.</span>
              </h2>
              <p className="text-[11px] min-[480px]:text-xs sm:text-base text-txt-muted leading-relaxed max-w-lg font-medium">
                Our technicians are stationed across 8 regional hubs in Maharashtra for rapid same-day response and certified audit compliance.
              </p>
            </div>

            {/* Direct Helpline Pods */}
            <div className="grid grid-cols-2 gap-2.5 min-[480px]:gap-3 sm:gap-4">
              <a href="tel:+918788895445" className="p-3 min-[480px]:p-4 sm:p-5 rounded-2xl sm:rounded-3xl bg-white border border-slate-200 shadow-md hover:border-cta hover:shadow-lg transition-all no-underline group">
                <div className="w-8 h-8 min-[480px]:w-9 min-[480px]:h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-accent-soft text-cta grid place-items-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform">
                  <SVGIcon name="phone" className="w-4 h-4 sm:w-5 sm:h-5 text-cta" />
                </div>
                <span className="text-[8px] min-[480px]:text-[9px] sm:text-[10px] font-bold text-txt-muted uppercase tracking-wider block">24/7 HELPLINE 1</span>
                <strong className="text-xs min-[480px]:text-sm sm:text-base font-extrabold text-primary group-hover:text-cta transition-colors">87888 95445</strong>
              </a>

              <a href="tel:+919850543737" className="p-3 min-[480px]:p-4 sm:p-5 rounded-2xl sm:rounded-3xl bg-white border border-slate-200 shadow-md hover:border-cta hover:shadow-lg transition-all no-underline group">
                <div className="w-8 h-8 min-[480px]:w-9 min-[480px]:h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-accent-soft text-cta grid place-items-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform">
                  <SVGIcon name="phone" className="w-4 h-4 sm:w-5 sm:h-5 text-cta" />
                </div>
                <span className="text-[8px] min-[480px]:text-[9px] sm:text-[10px] font-bold text-txt-muted uppercase tracking-wider block">24/7 HELPLINE 2</span>
                <strong className="text-xs min-[480px]:text-sm sm:text-base font-extrabold text-primary group-hover:text-cta transition-colors">98505 43737</strong>
              </a>
            </div>

            {/* Email & Office Address Card */}
            <div className="p-3.5 min-[480px]:p-4 sm:p-6 rounded-2xl sm:rounded-3xl bg-white border border-slate-200 shadow-md space-y-3 sm:space-y-4">
              <div className="flex items-start gap-2.5 sm:gap-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-accent-soft text-cta grid place-items-center shrink-0 mt-0.5">
                  <SVGIcon name="mapPin" className="w-4 h-4 sm:w-5 sm:h-5 text-cta" />
                </div>
                <div>
                  <span className="text-[8px] min-[480px]:text-[9px] sm:text-[10px] font-bold text-txt-muted uppercase tracking-wider block">OFFICE LOCATION</span>
                  <p className="text-[10px] min-[480px]:text-[11px] sm:text-xs text-primary font-semibold leading-relaxed mt-0.5">
                    Plot No-3/13, Hissa No-1180, Krushnamai Society, Taljai Pathar, Dhankawadi, Pune - 411043
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 sm:gap-4 pt-3 sm:pt-4 border-t border-slate-100">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-accent-soft text-cta grid place-items-center shrink-0">
                  <SVGIcon name="mail" className="w-4 h-4 sm:w-5 sm:h-5 text-cta" />
                </div>
                <div>
                  <span className="text-[8px] min-[480px]:text-[9px] sm:text-[10px] font-bold text-txt-muted uppercase tracking-wider block">EMAIL INQUIRIES</span>
                  <a href="mailto:pptpune2020@gmail.com" className="text-[10px] min-[480px]:text-[11px] sm:text-xs text-primary font-extrabold hover:text-cta transition-colors no-underline">
                    pptpune2020@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Redesigned 3D Glassmorphic WhatsApp Card */}
            <a
              href="https://wa.me/918788895445"
              target="_blank"
              rel="noreferrer"
              className="group relative block p-3.5 min-[480px]:p-4 sm:p-5 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-cta via-emerald-600 to-primary text-white no-underline shadow-xl hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 border border-white/20 overflow-hidden glow-cta"
            >
              {/* Subtle background shimmer overlay */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.25),transparent_70%)] pointer-events-none" />

              <div className="flex items-center justify-between gap-3 sm:gap-4 relative z-10">
                <div className="flex items-center gap-2.5 sm:gap-3.5">
                  {/* WhatsApp Official Icon Badge */}
                  <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 grid place-items-center shrink-0 shadow-md group-hover:scale-110 group-hover:rotate-6 transition-transform">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 fill-current text-white" viewBox="0 0 24 24">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                    </svg>
                  </div>

                  <div>
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5">
                      <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-accent-bright animate-ping shrink-0" />
                      <span className="text-[8px] min-[480px]:text-[9px] sm:text-[10px] font-extrabold tracking-widest text-accent-bright uppercase">INSTANT WHATSAPP DISPATCH</span>
                    </div>
                    <strong className="block text-xs min-[480px]:text-sm sm:text-base font-extrabold text-white leading-snug">
                      Need Immediate Help?
                    </strong>
                  </div>
                </div>

                <span className="px-2.5 min-[480px]:px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/20 backdrop-blur-md text-white text-[10px] sm:text-xs font-black tracking-wide border border-white/30 group-hover:bg-white group-hover:text-cta transition-colors shrink-0 shadow-sm flex items-center gap-1">
                  Open Chat <span className="group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </div>
            </a>
          </div>

          {/* Right Column: Modern 3D Inspection Booking Form */}
          <div className="lg:col-span-6">
            <div className="p-3.5 min-[480px]:p-4 sm:p-5 md:p-8 rounded-2xl sm:rounded-3xl md:rounded-[32px] shadow-2xl border-2 border-accent relative overflow-hidden bg-white">
              {/* Subtle Decorative Backdrop Glow */}
              <div className="absolute right-0 top-0 w-48 sm:w-64 h-48 sm:h-64 bg-white/40 rounded-full blur-3xl pointer-events-none" />

              <div className="mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-slate-100 relative z-10">
                <span className="text-[8px] min-[480px]:text-[9px] sm:text-[10px] font-extrabold uppercase tracking-widest text-cta bg-accent-soft px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full border border-accent/30 inline-block mb-1.5 sm:mb-2 shadow-2xs">
                  FREE ON-SITE ASSESSMENT
                </span>
                <h3 className="font-heading text-lg min-[480px]:text-xl sm:text-2xl md:text-3xl font-bold text-primary">
                  Request Free Inspection
                </h3>
                <p className="text-[10px] min-[480px]:text-[11px] sm:text-xs text-txt-secondary font-medium mt-0.5 sm:mt-1">
                  Fill in your details for fast technician dispatch within 24 hours.
                </p>
              </div>

              {isSubmitted ? (
                <div className="p-6 rounded-2xl bg-accent-soft border border-accent/40 text-center space-y-4 animate-rise my-6">
                  <div className="w-14 h-14 rounded-2xl bg-cta text-white grid place-items-center mx-auto shadow-md">
                    <SVGIcon name="check" className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="font-heading text-xl font-bold text-primary">Inspection Request Sent!</h4>
                  <p className="text-xs text-txt-muted leading-relaxed max-w-sm mx-auto">
                    Thank you <strong>{formData.name}</strong>. Our Pune dispatch team will call you back shortly at <strong>{formData.mobile}</strong>.
                  </p>
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: '', mobile: '', service: '', details: '' });
                    }}
                    className="mt-2 px-5 py-2.5 rounded-xl bg-primary text-white text-xs font-bold shadow-md hover:bg-primary-dark transition-all"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid gap-4 relative z-10" noValidate>
                  {/* Full Name */}
                  <label className="grid gap-1.5 text-[10px] font-extrabold text-primary tracking-wider uppercase">
                    <span>FULL NAME <span className="text-red-500">*</span></span>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className={`w-full px-4 py-3.5 rounded-xl border bg-white text-xs md:text-sm text-primary placeholder-txt-muted outline-none transition-all font-medium shadow-2xs ${errors.name ? 'border-red-500 ring-2 ring-red-500/20' : 'border-slate-300 focus:border-cta focus:ring-4 focus:ring-cta/15'
                        }`}
                    />
                    {errors.name && <span className="text-[11px] font-bold text-red-500 normal-case">{errors.name}</span>}
                  </label>

                  {/* Mobile Number */}
                  <label className="grid gap-1.5 text-[10px] font-extrabold text-primary tracking-wider uppercase">
                    <span>MOBILE NUMBER <span className="text-red-500">*</span></span>
                    <input
                      name="mobile"
                      type="tel"
                      maxLength={10}
                      inputMode="numeric"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="10-digit mobile number (e.g. 8788895445)"
                      className={`w-full px-4 py-3.5 rounded-xl border bg-white text-xs md:text-sm text-primary placeholder-txt-muted outline-none transition-all font-medium shadow-2xs ${errors.mobile ? 'border-red-500 ring-2 ring-red-500/20' : 'border-slate-300 focus:border-cta focus:ring-4 focus:ring-cta/15'
                        }`}
                    />
                    {errors.mobile && <span className="text-[11px] font-bold text-red-500 normal-case">{errors.mobile}</span>}
                  </label>

                  {/* Required Service */}
                  <label className="grid gap-1.5 text-[10px] font-extrabold text-primary tracking-wider uppercase">
                    <span>REQUIRED SERVICE PROGRAM <span className="text-red-500">*</span></span>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className={`w-full px-4 py-3.5 rounded-xl border bg-white text-xs md:text-sm text-primary outline-none transition-all font-medium shadow-2xs ${errors.service ? 'border-red-500 ring-2 ring-red-500/20' : 'border-slate-300 focus:border-cta focus:ring-4 focus:ring-cta/15'
                        }`}
                    >
                      <option value="" disabled>Select a service treatment</option>
                      {services.map((s) => <option key={s[0]} value={s[0]}>{s[0]}</option>)}
                    </select>
                    {errors.service && <span className="text-[11px] font-bold text-red-500 normal-case">{errors.service}</span>}
                  </label>

                  {/* Problem Details */}
                  <label className="grid gap-1.5 text-[10px] font-extrabold text-primary tracking-wider uppercase">
                    <span>PROBLEM DETAILS / PREMISE TYPE <span className="text-red-500">*</span></span>
                    <textarea
                      name="details"
                      rows={4}
                      value={formData.details}
                      onChange={handleChange}
                      placeholder="Tell us about the infestation type or premise size..."
                      className={`w-full px-4 py-3.5 rounded-xl border bg-white text-xs md:text-sm text-primary placeholder-txt-muted outline-none transition-all font-medium resize-none shadow-2xs ${errors.details ? 'border-red-500 ring-2 ring-red-500/20' : 'border-slate-300 focus:border-cta focus:ring-4 focus:ring-cta/15'
                        }`}
                    />
                    {errors.details && <span className="text-[11px] font-bold text-red-500 normal-case">{errors.details}</span>}
                  </label>

                  {errors.form && (
                    <div className="bg-red-50 text-red-600 border border-red-200 p-3.5 rounded-xl text-xs font-semibold">
                      {errors.form}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="btn-flash mt-2 bg-cta hover:bg-cta-hover text-white font-extrabold text-sm py-4 px-6 rounded-2xl flex items-center justify-center gap-2 glow-cta transition-all border-0 shadow-lg cursor-pointer disabled:opacity-70 disabled:cursor-wait"
                  >
                    {isSubmitting ? 'Sending Request...' : 'Submit Request'} <span>→</span>
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* Map Section */}
      <section className="py-14 sm:py-16 px-4 sm:px-6 md:px-16 max-w-6xl mx-auto">
        <div className="p-5 sm:p-8 md:p-12 rounded-3xl bg-white border border-slate-200 shadow-xl">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 sm:mb-8 gap-4">
            <div>
              <span className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 rounded-full bg-accent-soft text-accent-muted text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3 border border-accent/20 shadow-2xs">
                FIND OUR HEAD OFFICE
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary tracking-tight">
                Visit our <span className="text-cta">Pune office.</span>
              </h2>
            </div>
            <a href="https://www.google.com/maps/search/?api=1&query=Plot+No-3%2F13%2C+Hissa+No-1180%2C+Krushnamai+Society%2C+Taljai+Pathar%2C+Dhankawadi%2C+Pune+411043" target="_blank" rel="noreferrer" className="btn-flash text-white bg-primary hover:bg-primary-light px-5 sm:px-6 py-3 rounded-2xl text-xs font-bold shadow-md no-underline inline-flex items-center justify-center gap-2 shrink-0 w-full sm:w-auto text-center">
              Open in Google Maps →
            </a>
          </div>
          <iframe className="w-full h-[260px] sm:h-[340px] md:h-[430px] border-0 rounded-2xl shadow-md" title="Preventive Pest Terminators Pune office map" loading="lazy" referrerPolicy="no-referrer-when-downgrade" src="https://www.google.com/maps?q=Plot%20No-3%2F13%2C%20Hissa%20No-1180%2C%20Krushnamai%20Society%2C%20Taljai%20Pathar%2C%20Dhankawadi%2C%20Pune%20411043&output=embed" />
        </div>
      </section>

      {/* Curved Divider Top (Map -> Branch Locations) */}
      <div className="w-full overflow-hidden leading-none bg-bg -mt-1 -mb-1 relative z-10 pointer-events-none">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-14 text-accent-bright fill-current">
          <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,60 L1200,120 L0,120 Z" />
        </svg>
      </div>

      {/* Locations */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 md:px-16 bg-surface-alt text-center relative">
        <div className="max-w-5xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 rounded-full bg-accent-soft text-accent-muted text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3 sm:mb-4 border border-accent/20 shadow-2xs">
            MAHARASHTRA NETWORK
          </span>
          <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-primary tracking-tight mb-10 sm:mb-14">
            8 Regional <span className="text-cta">branch offices.</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {branches.map((x) => (
              <article
                key={x}
                className="group p-5 sm:p-6 text-left flex flex-col justify-between bg-white rounded-[28px] border-2 border-slate-200/90 hover:border-cta shadow-md hover:shadow-[0_15px_35px_rgba(23,75,53,0.15),0_0_20px_rgba(169,217,87,0.35)] transition-all duration-700 cursor-pointer relative overflow-hidden"
              >
                {/* Flashing Glass Sheen Overlay */}
                <div className="absolute inset-0 -left-full w-full bg-gradient-to-r from-transparent via-white/50 to-transparent skew-x-12 group-hover:left-[200%] transition-all duration-1000 ease-in-out pointer-events-none z-10" />

                {/* Slow Radar Sonar Pulse Wave expanding to Bottom-Right (1.6s) */}
                <div className="absolute top-6 left-6 w-32 h-32 -mt-6 -ml-6 rounded-full border border-cta/30 opacity-0 group-hover:opacity-100 scale-50 group-hover:animate-ping pointer-events-none z-0" />
                <div className="absolute top-6 left-6 w-[450px] h-[450px] -mt-24 -ml-24 rounded-full bg-gradient-to-br from-accent/30 via-cta/20 to-primary/10 opacity-0 group-hover:opacity-100 scale-0 group-hover:scale-100 transition-all duration-[1600ms] ease-out origin-top-left pointer-events-none z-0 shadow-xl" />

                <div className="relative z-20">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-accent-soft group-hover:bg-cta group-hover:text-white border border-accent/40 text-cta flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-2xs relative">
                    <SVGIcon name="mapPin" className="w-5 h-5 sm:w-6 sm:h-6 fill-current" />
                    {/* Node Pulse Beacon */}
                    <span className="absolute inset-0 rounded-2xl bg-cta opacity-0 group-hover:animate-ping pointer-events-none" />
                  </div>
                  <h3 className="font-heading text-base font-extrabold text-primary mb-1 group-hover:text-cta transition-colors duration-500">{x}</h3>
                  <p className="text-xs text-txt-muted font-medium">{x.includes('Pune') ? 'Headquarters & Central Dispatch' : 'Regional Fast Dispatch Office'}</p>
                </div>

                <div className="pt-3 mt-4 border-t border-slate-100 group-hover:border-accent/30 flex items-center justify-between text-[10px] sm:text-[11px] font-bold text-accent-muted group-hover:text-cta transition-colors duration-500 relative z-20">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-success animate-ping shrink-0" /> Active Station
                  </div>
                  <span className="group-hover:translate-x-1 transition-transform duration-500">→</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
